import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import CopyButton from "components/common/CopyButton";
import CustomButton from "components/common/CustomButton";
import api from "features/api";
import {
  browserProfileTag,
  useCancelBrowserWarmingMutation,
  useGetBrowserWarmingSessionQuery,
  useSaveBrowserWarmingMutation,
  useStartBrowserWarmingMutation,
  WARMING_TERMINAL_STATUSES,
} from "features/integrationsBeta/browserProfiles";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useRef, useState } from "react";
import { formatApiError } from "utils/formatApiError";

const LOCAL_WORKER_COMMAND =
  "DB_HOST=localhost newenv/bin/python manage.py browser_warming_worker";

const POLL_MS = 2000;

/**
 * Operator-driven warming of a BOT connection's browser profile.
 *
 * The dialog only talks to the warming-session API: start (idempotent -
 * reopening resumes the active session), poll, save, cancel. Where the
 * browser runs depends on the stand: deployed, the ECS warming task serves
 * it and the dialog embeds its live noVNC view; on LOCAL_DEV the developer's
 * host worker opens Chrome on their desktop and the dialog shows the command.
 */
export default function BrowserWarmingDialog({
  connectionId,
  connectionName,
  open,
  onClose,
}: {
  connectionId: number;
  connectionName: string;
  open: boolean;
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  // Pinned on first appearance: every poll re-mints the token, and swapping
  // the iframe src each poll would reload the VNC view every 2 seconds.
  const [liveUrl, setLiveUrl] = useState<string | null>(null);

  const [startWarming] = useStartBrowserWarmingMutation();
  const [saveWarming, { isLoading: saving }] = useSaveBrowserWarmingMutation();
  const [cancelWarming, { isLoading: cancelling }] =
    useCancelBrowserWarmingMutation();

  const startedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setSessionId(null);
      setStartError(null);
      setLiveUrl(null);
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    startWarming(connectionId)
      .unwrap()
      .then((session) => setSessionId(session.id))
      .catch((err) =>
        setStartError(
          formatApiError(
            (err as { data?: unknown })?.data,
            "Could not start the warming session.",
          ),
        ),
      );
  }, [open, connectionId, startWarming]);

  // Conditional polling needs a state cell: the hook's options cannot read
  // the hook's own result. Poll while the session is live, stop on terminal.
  const [polling, setPolling] = useState(true);
  const { data: session } = useGetBrowserWarmingSessionQuery(sessionId ?? 0, {
    skip: !open || sessionId === null,
    pollingInterval: polling ? POLL_MS : 0,
  });
  const status = session?.status;
  const terminal =
    status !== undefined && WARMING_TERMINAL_STATUSES.includes(status);
  useEffect(() => setPolling(!terminal), [terminal]);

  useEffect(() => {
    if (session?.live_view_url && !liveUrl) setLiveUrl(session.live_view_url);
  }, [session?.live_view_url, liveUrl]);

  // A finished session changes the profile's warmed state on the card.
  const notifiedRef = useRef(false);
  useEffect(() => {
    if (status !== "DONE" || notifiedRef.current) return;
    notifiedRef.current = true;
    dispatch(api.util.invalidateTags([browserProfileTag(connectionId)]));
    dispatch(
      pushNotification({
        severity: "success",
        message: "Browser profile warmed and published.",
      }),
    );
  }, [status, connectionId, dispatch]);

  const handleSave = async () => {
    if (sessionId === null) return;
    try {
      await saveWarming(sessionId).unwrap();
    } catch (err) {
      dispatch(
        pushNotification({
          severity: "error",
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not request the save.",
          ),
        }),
      );
    }
  };

  const handleCancel = async () => {
    setConfirmCancel(false);
    if (sessionId === null) return;
    try {
      await cancelWarming(sessionId).unwrap();
    } catch (err) {
      dispatch(
        pushNotification({
          severity: "error",
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not cancel the session.",
          ),
        }),
      );
    }
  };

  // Deployed live view: fullscreen overlay with the task's noVNC embedded.
  // Kept up through SAVING (Chrome is closing, the view goes dark) and handed
  // back to the compact dialog on a terminal status to show the outcome.
  const liveActive =
    liveUrl !== null && (status === "READY" || status === "SAVING");

  return (
    <>
      <Dialog fullScreen open={open && liveActive} onClose={() => undefined}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
            Warm browser profile - {connectionName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Log in, solve the captcha, tick "remember this device", then save.
          </Typography>
          <CustomButton
            fullWidth={false}
            disabled={cancelling}
            onClick={() => setConfirmCancel(true)}
          >
            Cancel session
          </CustomButton>
          <CustomButton
            fullWidth={false}
            disabled={status !== "READY" || saving}
            onClick={handleSave}
          >
            Save profile
          </CustomButton>
          <Tooltip title="Hide (the session keeps running)">
            <IconButton onClick={onClose} aria-label="Hide live view">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1, position: "relative", bgcolor: "black" }}>
          {status === "SAVING" ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                color: "common.white",
              }}
            >
              <CircularProgress size={24} color="inherit" />
              <Typography>
                Closing the browser and publishing the profile...
              </Typography>
            </Box>
          ) : (
            liveUrl && (
              <iframe
                title="Warming browser live view"
                src={liveUrl}
                style={{
                  border: 0,
                  width: "100%",
                  height: "100%",
                  display: "block",
                }}
              />
            )
          )}
        </Box>
      </Dialog>

      <AppDialog
        open={open && !liveActive}
        onClose={onClose}
        title={`Warm browser profile - ${connectionName}`}
        maxWidth="sm"
        showCloseIcon
        actions={
          terminal || startError ? (
            <CustomButton fullWidth={false} onClick={onClose}>
              Close
            </CustomButton>
          ) : (
            <>
              <CustomButton
                fullWidth={false}
                disabled={cancelling || sessionId === null}
                onClick={() => setConfirmCancel(true)}
              >
                Cancel session
              </CustomButton>
              <CustomButton
                fullWidth={false}
                disabled={status !== "READY" || saving}
                onClick={handleSave}
              >
                Save profile
              </CustomButton>
            </>
          )
        }
      >
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {startError && <Alert severity="error">{startError}</Alert>}

          {!startError &&
            (status === undefined || status === "PROVISIONING") && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {session?.requires_local_worker
                      ? "Waiting for the local warming worker to pick the session up..."
                      : "Starting the warming browser task..."}
                  </Typography>
                </Box>
                {session?.requires_local_worker && (
                  <Alert severity="info">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Local stand: run the worker on the host (a container has
                      no desktop). Chrome opens on your desktop.
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontFamily: "monospace",
                        fontSize: 13,
                        p: 1,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        overflowX: "auto",
                      }}
                    >
                      <span>{LOCAL_WORKER_COMMAND}</span>
                      <CopyButton value={LOCAL_WORKER_COMMAND} />
                    </Box>
                  </Alert>
                )}
              </>
            )}

          {status === "READY" && (
            <>
              <Alert severity="info">
                The browser is open on the connection's profile.
              </Alert>
              <Typography variant="body2" component="div">
                In that browser window:
                <ol style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                  <li>log in to the portal;</li>
                  <li>solve the captcha if one appears;</li>
                  <li>tick "remember this device" if the portal offers it;</li>
                  <li>come back here and press "Save profile".</li>
                </ol>
              </Typography>
            </>
          )}

          {status === "SAVING" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                Closing the browser and publishing the profile...
              </Typography>
            </Box>
          )}

          {status === "DONE" && (
            <Alert severity="success">
              Profile warmed and published. Bot runs of this connection will
              reuse it.
            </Alert>
          )}

          {status === "FAILED" && (
            <Alert severity="error">
              {session?.error_message || "The warming session failed."}
            </Alert>
          )}

          {status === "CANCELLED" && (
            <Alert severity="warning">
              Session cancelled.{" "}
              {session?.error_message ? session.error_message : ""}
            </Alert>
          )}

          {!terminal && !startError && (
            <Typography variant="caption" color="text.secondary">
              Closing this window does not stop the session - reopen the dialog
              to resume it. An untouched session expires on its own.
            </Typography>
          )}
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel warming session"
        message="Close the browser without saving the profile?"
        confirmLabel="Cancel session"
        cancelLabel="Keep going"
        loading={cancelling}
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancel(false)}
      />
    </>
  );
}
